import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiFile, FiDownload, FiPlus, FiUpload, FiX, FiTrash2 } from 'react-icons/fi';
import { get, post, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

interface DocumentSummary {
  documentId: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  contentType: string;
  s3Key: string;
}

interface CustomerDocumentsResponse {
  customerId: string;
  documents: DocumentSummary[];
  total: number;
}

const QNavCustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string>('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadCustomerDocuments();
    }
  }, [customerId]);

  const loadCustomerDocuments = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await get({
        apiName: 'api',
        path: `/api/customers/${customerId}/documents`,
        options: {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      }).response;

      const data = (await response.body.json()) as any;
      setDocuments(data.documents || []);

      // Try to get customer name from the browse API since the documents API might not return it
      const browseResponse = await get({
        apiName: 'api',
        path: '/api/customers/browse',
        options: {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      }).response;

      const browseData = (await browseResponse.body.json()) as any;
      const customer = browseData.customers?.find((c: any) => c.customerId === customerId);
      setCustomerName(customer?.customerName || customer?.name || customerId || '');
    } catch (err) {
      setError('Failed to load customer documents');
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownload = async (document: DocumentSummary) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      // Get presigned download URL
      const response = await get({
        apiName: 'api',
        path: `/api/documents/${encodeURIComponent(document.documentId)}/download`,
        options: {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      }).response;

      const data = (await response.body.json()) as any;

      // Open download URL in new tab
      window.open(data.downloadUrl, '_blank');
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download document');
    }
  };

  const handleDelete = async (document: DocumentSummary) => {
    if (!window.confirm(`Are you sure you want to delete "${document.fileName}"?`)) {
      return;
    }

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      // Delete document
      await del({
        apiName: 'api',
        path: `/api/documents/${document.documentId}`,
        options: {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      }).response;

      // Refresh document list
      await loadCustomerDocuments();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete document');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !customerId) return;

    try {
      setUploading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      // Map file type to API enum values
      const getFileType = (file: File): string => {
        const type = file.type.toLowerCase();
        if (type === 'application/pdf') return 'application/pdf';
        if (type === 'application/msword') return 'application/msword';
        if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
          return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (type === 'application/vnd.ms-excel') return 'application/vnd.ms-excel';
        if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
          return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (type === 'text/plain') return 'text/plain';
        if (type === 'text/markdown') return 'text/markdown';
        if (type === 'text/html') return 'text/html';
        if (type === 'text/csv') return 'text/csv';
        // Return the actual file type instead of defaulting to PDF
        return file.type || 'application/octet-stream';
      };

      // Get presigned upload URL
      const uploadResponse = await post({
        apiName: 'api',
        path: '/api/documents/upload',
        options: {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: {
            customerId: customerId,
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            fileType: getFileType(selectedFile),
          },
        },
      }).response;

      const uploadData = (await uploadResponse.body.json()) as any;

      // Upload file to S3 using presigned URL
      const s3Response = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
          'x-amz-server-side-encryption': 'AES256',
        },
      });

      if (!s3Response.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Refresh document list
      await loadCustomerDocuments();

      // Close modal and reset state
      setShowUploadModal(false);
      setSelectedFile(null);
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadCustomerDocuments} className="qnav-btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Header */}
      <div className="bg-brand-gradient text-white p-4 rounded-qnav shadow-brand-card">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/dashboard/customers')}
            className="mr-4 p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FiArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-display font-bold">{customerName}</h1>
            <p className="text-secondary-200 mt-1">{documents.length} documents available</p>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="flex-1 bg-surface rounded-qnav shadow-brand-card border border-secondary-200">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-secondary-800">Customer Documents</h2>
            <button onClick={() => setShowUploadModal(true)} className="qnav-btn-primary flex items-center">
              <FiPlus className="mr-2" size={16} />
              Upload Document
            </button>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-12 text-secondary-500">
              <FiFile size={48} className="mx-auto mb-4 text-secondary-300" />
              <p className="text-lg mb-2">No documents found</p>
              <p className="text-sm">This customer doesn't have any documents yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Document
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 relative">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {documents.map((document) => (
                    <tr key={document.documentId} className="hover:bg-secondary-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FiFile className="text-primary-600 mr-3" size={20} />
                          <div>
                            <button
                              onClick={() => handleDownload(document)}
                              className="text-sm font-medium text-primary-600 hover:text-primary-800 cursor-pointer underline"
                            >
                              {document.fileName}
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatFileSize(document.fileSize)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                        {formatDate(document.uploadedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">{document.contentType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleDownload(document)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Download document"
                          >
                            <FiDownload size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(document)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete document"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Upload Document</h3>
              <button onClick={() => setShowUploadModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <FiX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">Select File</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-secondary-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <FiUpload className="mx-auto h-12 w-12 text-secondary-400" />
                    <div className="flex justify-center text-sm text-secondary-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileSelect}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        />
                      </label>
                    </div>
                    <p className="text-xs text-secondary-500 text-center">
                      PDF, DOC, DOCX, XLS, XLSX, TXT, CSV up to 10MB
                    </p>
                    {selectedFile && (
                      <p className="text-sm text-primary-600 font-medium">Selected: {selectedFile.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50"
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  'Upload Document'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QNavCustomerDetail;
